import React, { Suspense, useEffect, useState } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { Outlet, useNavigate } from 'react-router-dom';

// hooks
import { useRedux, useSubscription, useUser } from '../hooks/';

// constants
import { LayoutTypes, SideBarTypes } from '../constants';

// utils
import { changeBodyAttribute } from '../utils';

// code splitting and lazy loading
// https://blog.logrocket.com/lazy-loading-components-in-react-16-6-6cea535c0b52
const Topbar = React.lazy(() => import('./Topbar/'));
const LeftSidebar = React.lazy(() => import('./LeftSidebar'));
const RightSidebar = React.lazy(() => import('./RightSidebar'));
const Footer = React.lazy(() => import('./Footer'));

const loading = () => <div className=""></div>;

const VerticalLayout = () => {
    const { appSelector } = useRedux();
    const [loggedInUser] = useUser();
    const { subscription } = useSubscription();
    const navigate = useNavigate();

    const {
        layoutColor,
        layoutWidth,
        menuPosition,
        leftSideBarTheme,
        leftSideBarType,
        showSidebarUserInfo,
        topbarTheme,
    } = appSelector((state) => ({
        layoutColor: state.Layout.layoutColor,
        layoutWidth: state.Layout.layoutWidth,
        menuPosition: state.Layout.menuPosition,
        leftSideBarTheme: state.Layout.leftSideBarTheme,
        leftSideBarType: state.Layout.leftSideBarType,
        showSidebarUserInfo: state.Layout.showSidebarUserInfo,
        topbarTheme: state.Layout.topbarTheme,
    }));

    const [isMenuOpened, setIsMenuOpened] = useState<boolean>(false);

    /*
  layout defaults
  */
    useEffect(() => {
        changeBodyAttribute('data-layout-mode', LayoutTypes.LAYOUT_VERTICAL);
    }, []);

    useEffect(() => {
        changeBodyAttribute('data-layout-color', layoutColor);
    }, [layoutColor]);

    useEffect(() => {
        changeBodyAttribute('data-layout-size', layoutWidth);
    }, [layoutWidth]);

    useEffect(() => {
        changeBodyAttribute('data-leftbar-position', menuPosition);
    }, [menuPosition]);

    useEffect(() => {
        changeBodyAttribute('data-leftbar-color', leftSideBarTheme);
    }, [leftSideBarTheme]);

    useEffect(() => {
        changeBodyAttribute('data-leftbar-size', leftSideBarType);
    }, [leftSideBarType]);

    useEffect(() => {
        changeBodyAttribute('data-topbar-color', topbarTheme);
    }, [topbarTheme]);

    useEffect(() => {
        changeBodyAttribute('data-sidebar-user', showSidebarUserInfo);
    }, [showSidebarUserInfo]);

    /**
     * Open the menu when having mobile screen
     */
    const openMenu = () => {
        setIsMenuOpened((prevState) => !prevState);

        if (document.body) {
            if (isMenuOpened) {
                document.body.classList.remove('sidebar-enable');
            } else {
                document.body.classList.add('sidebar-enable');
            }
        }
    };

    const isCondensed: boolean = leftSideBarType === SideBarTypes.LEFT_SIDEBAR_TYPE_CONDENSED;
    const userRole = String(loggedInUser?.role || '');
    const isFranchiseUser = ['franchise_admin', 'staff'].includes(userRole);

    const resolveSubscriptionEndDate = () => {
        if (!subscription) return null;

        // Preferred source: Stripe/current period end from backend
        if (subscription.currentPeriodEnd) {
            const parsed = new Date(subscription.currentPeriodEnd);
            if (!Number.isNaN(parsed.getTime())) {
                return parsed;
            }
        }

        // Fallback source: cancellation date set from Stripe cancel_at
        if (subscription.cancelledAt) {
            const cancelledAtDate = new Date(subscription.cancelledAt);
            if (!Number.isNaN(cancelledAtDate.getTime())) {
                return cancelledAtDate;
            }
        }

        // Last fallback: estimate from createdAt + plan interval
        const createdAtRaw = (subscription as any)?.createdAt;
        const rawPlan = (subscription as any)?.subscriptionPlanId;
        const interval =
            rawPlan && typeof rawPlan === 'object' && rawPlan?.billingInterval
                ? String(rawPlan.billingInterval)
                : 'month';
        if (createdAtRaw) {
            const estimated = new Date(createdAtRaw);
            if (!Number.isNaN(estimated.getTime())) {
                switch (interval) {
                    case 'day':
                        estimated.setDate(estimated.getDate() + 1);
                        break;
                    case 'week':
                        estimated.setDate(estimated.getDate() + 7);
                        break;
                    case 'year':
                        estimated.setFullYear(estimated.getFullYear() + 1);
                        break;
                    case 'month':
                    default:
                        estimated.setMonth(estimated.getMonth() + 1);
                        break;
                }
                return estimated;
            }
        }

        return null;
    };

    const subscriptionEndDate = resolveSubscriptionEndDate();
    const showSubscriptionEndBanner =
        isFranchiseUser &&
        !!subscription?.cancelledAtPeriodEnd &&
        !!subscriptionEndDate &&
        subscriptionEndDate.getTime() > Date.now();

    const subscriptionEndDateLabel = subscriptionEndDate ? subscriptionEndDate.toLocaleDateString() : '';

    return (
        <>
            <div id="wrapper">
                <Suspense fallback={loading()}>
                    <Topbar openLeftMenuCallBack={openMenu} />
                </Suspense>
                <Suspense fallback={loading()}>
                    <LeftSidebar isCondensed={isCondensed} />
                </Suspense>
                <div className="content-page" style={{ position: 'relative' }}>
                    <div className="content">
                        <Container fluid>
                            {showSubscriptionEndBanner && (
                                <Alert variant="warning" className="mt-2 mb-3 ha-subscription-end-banner">
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                        <div>
                                            Your subscription will end on <strong>{subscriptionEndDateLabel}</strong>.
                                            After this date, subscribed features will be restricted unless renewed.
                                        </div>
                                        {userRole === 'franchise_admin' && (
                                            <Button
                                                size="sm"
                                                className="ha-btn ha-btn-primary"
                                                onClick={() =>
                                                    navigate('/subscription', {
                                                        state: { openRenewModal: true },
                                                    })
                                                }>
                                                Renew Subscription
                                            </Button>
                                        )}
                                    </div>
                                </Alert>
                            )}
                            <Outlet />
                        </Container>
                    </div>
                </div>
            </div>

            <Suspense fallback={loading()}>
                <RightSidebar />
            </Suspense>
        </>
    );
};

export default VerticalLayout;
